import React, { useEffect, useRef, useState } from 'react'
import { makeAutoObservable } from 'mobx'
import { observer } from 'mobx-react-lite'

import { Box, FormHelperText, FormLabel, Grid } from '@material-ui/core'

import { MoySkladStatusMapForm } from './MoySkladStatusMapForm'

import { useBoolState, useLazyEffect } from '../hooks/commonHooks'
import { useMessages } from '../hooks/snackbarHooks'
import { useApi, useStore } from '../hooks/storeHook'
import clsx from 'clsx'

import { MoySkladStatusMapsModel } from '../server/mpsklad_core/Models/MoySkladStatusMapsModel'
import { useSettingsPageStyles } from '../hooks/settingsPageStylesHook'

export type MoySkladStatusMapPanelProps = {
  shouldSkipMapButton?: boolean,
  shouldShowHelpHints?: boolean
}

export const MoySkladStatusMapPanel =
  observer(
    ({shouldSkipMapButton, shouldShowHelpHints}: MoySkladStatusMapPanelProps) => {
      const {showError} = useMessages()
      const formRef = useRef<HTMLDivElement>(null)
      const hintsContainerRef = useRef<HTMLDivElement>(null)

      const {syncStore} = useStore()

      const api = useApi()
      const [isLoading, setIsLoading, setNotLoading] = useBoolState()

      const classes = useSettingsPageStyles()

      const [statusMaps, setStatusMaps] = useState<MoySkladStatusMapsModel | null>(null)

      const onLoad = async () => {
        setIsLoading()

        try {
          const maps = await api.userSync.getMoySkladStatusMaps()
          setStatusMaps(makeAutoObservable(maps))
        } catch (e) {
          console.error('Failed to load MoySklad status maps', e)
          showError('Не удалось загрузить')
        } finally {
          setNotLoading()
        }
      }

      useLazyEffect(async () => {
        if (!shouldSkipMapButton) {
          return
        }

        setIsLoading()

        try {
          await onLoad()
          setNotLoading()
        } catch (e) {
          console.error('Failed to load MoySklad status maps', e)
          showError('Не удалось загрузить')
        }
      }, [shouldSkipMapButton])

      useEffect(() => {
        const updatePosition = () => {
          if (formRef.current && hintsContainerRef.current) {
            const formRect = formRef.current.getBoundingClientRect()
            const newLeft = formRect.right + 40
            hintsContainerRef.current.style.left = `${newLeft}px`
          }
        }

        updatePosition()

        window.addEventListener('resize', updatePosition)
        return () => {
          window.removeEventListener('resize', updatePosition)
        }
      }, [statusMaps, shouldShowHelpHints])

      if (!syncStore.moySkladAccount) {
        return null
      }

      return (
        <Box className={classes.pageContainer}>

          {shouldShowHelpHints &&
           <div className={classes.hintsContainer} ref={hintsContainerRef}>
             <Box className={clsx(classes.helpBox, 'default-border')}>
               <FormLabel className={classes.helpBoxLabel}>
                 Зачем это нужно:
               </FormLabel>
               <br/>
               <br/>
               <FormHelperText>
                 1. Автоматического обновления статусов на всех маркетплейсах.
                 <br/>
                 2. Снижения ошибок и несоответствий при обработке заказов.
                 <br/>
                 3. Повышения эффективности работы
                 и информирования клиентов.
               </FormHelperText>

             </Box>

             <Box className={clsx(classes.helpActionsBox, 'default-border')}>
               <FormHelperText>
                 Создайте недастоющие статусы
                 в МоёмСкладе.
                 <br/>
                 <br/>
                 Откройте <a className={classes.link}
                             href="https://help.mp360.ru/%D0%BD%D0%B0%D1%81%D1%82%D1%80%D0%BE%D0%B9%D0%BA%D0%B0-%D0%BF%D1%80%D0%B8%D0%BB%D0%BE%D0%B6%D0%B5%D0%BD%D0%B8%D1%8F.html"
                             target="_blank" rel="noopener noreferrer">
                 этот раздел</a> в новом окне
                 <br/>
                 <br/>
                 Подробнее в <a className={classes.link} href="https://help.mp360.ru/" target="_blank"
                                rel="noopener noreferrer">
                 инструкции</a>
               </FormHelperText>
             </Box>
           </div>}

          <Grid container className={clsx(classes.settingsTopic, 'default-border')} ref={formRef}>
            <Grid item className={classes.root} xs={12}>

              {
                statusMaps === null
                ? isLoading
                  ? 'загружаем'
                  :
                  <Box width={'100%'} display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <FormLabel className={classes.boldLabel}>
                        Сопоставление статусов заказов
                      </FormLabel>

                      <FormHelperText className={classes.helperText}>
                        {syncStore.moySkladAccount.hasStatusMaps
                         ? 'Статусы сопоставлены'
                         : 'Статусы не сопоставлены'}
                      </FormHelperText>
                    </Box>


                    <button
                      className={clsx(classes.saveButton, 'default-button')}
                      style={{margin: 0}}
                      disabled={isLoading}
                      onClick={onLoad}>
                      <p>
                        {
                          syncStore.moySkladAccount.hasStatusMaps
                          ? 'Изменить'
                          : 'Сопоставить'
                        }
                      </p>
                    </button>

                  </Box>
                :
                <>
                  <FormLabel className={classes.headerMapPanel}>
                    Сопоставление статусов заказов
                  </FormLabel>
                  <MoySkladStatusMapForm statusMaps={statusMaps} resetStatusMaps={() => setStatusMaps(null)}/>
                </>
              }
            </Grid>
          </Grid>

        </Box>
      )
    })